import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Badge, Alert, Dropdown, Form } from 'react-bootstrap';
import fileDownload from 'js-file-download';
import api from "../../services/api";
import Loading from '../Shared/Loading';
import TableFull from '../Shared/CommonTable/TableFull';
import { toast } from 'react-toastify';
import { dateOnlyAsNumbers } from '../../utils/dateFormatter';
import styles from './FirmwareManagement.module.scss';

// Mirrors FirmwareManager.UploadVersionPattern (no leading zeros); the server is authoritative
const VERSION_PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;

export default function FirmwareManagement() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [items, setItems] = useState([]);
  const [processing, setProcessing] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDeviceType, setUploadDeviceType] = useState('');
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadIsActive, setUploadIsActive] = useState(true);
  const [uploadFile, setUploadFile] = useState(null);

  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    loadFirmwareList();
  }, []);

  const loadFirmwareList = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/admin/firmware');
      setItems(response.data.items);
      setLoadFailed(false);
    } catch (error) {
      setLoadFailed(true);
      toast.error(t('admin.firmware.loadError'));
      console.error('Error loading firmware list:', error);
    } finally {
      setLoading(false);
    }
  };

  const openUploadModal = () => {
    setUploadDeviceType('');
    setUploadVersion('');
    setUploadDescription('');
    setUploadIsActive(true);
    setUploadFile(null);
    setShowUploadModal(true);
  };

  const canSubmitUpload = uploadDeviceType && VERSION_PATTERN.test(uploadVersion.trim()) && uploadFile;

  const submitUpload = async () => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('DeviceType', uploadDeviceType);
      formData.append('Version', uploadVersion.trim());
      if (uploadDescription) {
        formData.append('Description', uploadDescription);
      }
      formData.append('IsActive', uploadIsActive);
      formData.append('file', uploadFile, uploadFile.name);

      const response = await api.post('/settings/admin/firmware/upload', formData);
      if (response.data) {
        // Non-empty body is a localized error message from the backend
        toast.error(response.data);
      } else {
        toast.success(t('admin.firmware.uploadSuccess'));
        setShowUploadModal(false);
        await loadFirmwareList();
      }
    } catch (error) {
      toast.error(t('admin.firmware.uploadError'));
      console.error('Error uploading firmware:', error);
    } finally {
      setProcessing(false);
    }
  };

  const toggleActive = async (item) => {
    setProcessing(true);
    try {
      await api.post(`/settings/admin/firmware/${item.uid}/set-active`, { isActive: !item.isActive });
      await loadFirmwareList();
    } catch (error) {
      toast.error(t('admin.firmware.actionError'));
      console.error('Error toggling firmware active state:', error);
    } finally {
      setProcessing(false);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    setProcessing(true);
    try {
      await api.post(`/settings/admin/firmware/${itemToDelete.uid}/delete`);
      toast.success(t('admin.firmware.deleteSuccess'));
      await loadFirmwareList();
    } catch (error) {
      toast.error(t('admin.firmware.actionError'));
      console.error('Error deleting firmware:', error);
    } finally {
      setProcessing(false);
      setItemToDelete(null);
    }
  };

  const downloadItem = (item) => {
    api.get(`/settings/admin/firmware/${item.uid}/download`, { responseType: 'blob' })
      .then((response) => fileDownload(response.data, item.originalFileName))
      .catch((error) => {
        toast.error(t('admin.firmware.actionError'));
        console.error('Error downloading firmware:', error);
      });
  };

  const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const columns = useMemo(() => [
    {
      header: t('admin.firmware.device'),
      accessorKey: 'deviceName',
    },
    {
      header: t('admin.firmware.version'),
      accessorKey: 'version',
    },
    {
      header: t('admin.firmware.fileName'),
      accessorKey: 'originalFileName',
    },
    {
      header: t('admin.firmware.fileSize'),
      accessorKey: 'fileSizeBytes',
      cell: info => formatSize(info.getValue()),
    },
    {
      header: t('admin.firmware.description'),
      accessorKey: 'description',
      cell: info => info.getValue() || '-',
    },
    {
      header: t('admin.firmware.statusHeader'),
      accessorKey: 'isActive',
      cell: info => info.getValue()
        ? <Badge bg="success">{t('admin.firmware.active')}</Badge>
        : <Badge bg="secondary">{t('admin.firmware.inactive')}</Badge>,
    },
    {
      header: t('admin.firmware.uploaded'),
      accessorKey: 'dateCreatedUtc',
      cell: info => dateOnlyAsNumbers(info.getValue()),
    },
    {
      header: t('admin.firmware.actions'),
      id: 'actions',
      cell: ({ row }) => (
        <div className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              bsPrefix="btn"
              disabled={processing}
              title={t('admin.firmware.actionsMenu')}
            >
              <i className="bi bi-three-dots-vertical"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => downloadItem(row.original)}>
                {t('admin.firmware.download')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => toggleActive(row.original)}>
                {row.original.isActive ? t('admin.firmware.deactivate') : t('admin.firmware.activate')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setItemToDelete(row.original)}>
                {t('admin.firmware.delete')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
    },
  ], [t, processing]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerActions}>
          <Button variant="primary" onClick={openUploadModal} disabled={processing}>
            {t('admin.firmware.uploadButton')}
          </Button>
          <Button variant="outline-primary" onClick={loadFirmwareList}>
            {t('admin.firmware.refresh')}
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className={styles.emptyState}>
          {/* A failed load must not present itself as an empty registry */}
          <Alert variant={loadFailed ? 'danger' : 'info'}>
            {loadFailed ? t('admin.firmware.loadError') : t('admin.firmware.noFirmware')}
          </Alert>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          {/* Initial order comes from the API (device type, then newest first); TableFull's
              controlled state ignores initialStateSettings, so none is passed here. */}
          <TableFull
            data={items}
            columns={columns}
            styles={styles}
            allowColumnHeaderSort={true}
            mainTableClassName={styles.table}
          />
        </div>
      )}

      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.firmware.uploadTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.firmware.device')}</Form.Label>
            <Form.Select value={uploadDeviceType} onChange={e => setUploadDeviceType(e.target.value)}>
              <option value="" disabled hidden>{t('admin.firmware.selectDevice')}</option>
              <option value="1">{t('admin.firmware.deviceTypeNames.1')}</option>
              <option value="2">{t('admin.firmware.deviceTypeNames.2')}</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('admin.firmware.version')}</Form.Label>
            <Form.Control
              type="text"
              value={uploadVersion}
              onChange={e => setUploadVersion(e.target.value)}
              placeholder={t('admin.firmware.versionPlaceholder')}
              isInvalid={uploadVersion !== '' && !VERSION_PATTERN.test(uploadVersion.trim())}
            />
            <Form.Control.Feedback type="invalid">
              {t('admin.firmware.versionFormatError')}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('admin.firmware.description')}</Form.Label>
            <Form.Control
              type="text"
              maxLength={1000}
              value={uploadDescription}
              onChange={e => setUploadDescription(e.target.value)}
              placeholder={t('admin.firmware.descriptionPlaceholder')}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('admin.firmware.file')}</Form.Label>
            <Form.Control
              type="file"
              onChange={e => setUploadFile(e.target.files[0])}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label={t('admin.firmware.activeImmediately')}
              checked={uploadIsActive}
              onChange={e => setUploadIsActive(e.target.checked)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={submitUpload} disabled={!canSubmitUpload || processing}>
            {processing && (<span className="spinner-border spinner-border-sm me-1"></span>)}
            {t('admin.firmware.uploadButton')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!itemToDelete} onHide={() => setItemToDelete(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.firmware.confirmDeleteTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {itemToDelete && (
            <>
              <p>
                <strong>{t('admin.firmware.device')}:</strong> {itemToDelete.deviceName}{' '}
                <strong>{t('admin.firmware.version')}:</strong> {itemToDelete.version}
              </p>
              <Alert variant="warning">
                {t('admin.firmware.warningDelete')}
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setItemToDelete(null)}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={processing}>
            {t('common.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
